import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { LoadingSpinner, Dropdown, styles } from 'yankees-react-utils';
import { useOktaQueries, Advance, Player } from '../../../../data';
import StateTabs from '../../../../components/StateTabs';
import StealingAgainstPitchers from './StealingAgainstPitchers';
import StealingAgainstCatchers from './StealingAgainstCatchers';
import StealingAgainstPitchouts from './StealingAgainstPitchouts';
import useConfig from '../../../../utils/useConfig';
import { buildStealingAgainstTimeCharts, roundPitcherAvgTimes } from './utils';

const StyledStealingAgainstReport = styled.div`
    .border-bottom-2 {
        border-bottom: 2px solid ${styles.theme.fontColors.mineShaft} !important;
    }
    .stoplightgreen {
        background-color: #33FF33 !important;
    }
    .stoplightyellow {
        background-color: #FFFF33 !important;
    }
`;

const buildQueries = (teamid, yearid, levelid) => {
    const queries = [
        {
            queryKey: ['teamRoster', teamid, 'pitcher'],
            queryFn: (t) => Player.list(t, { teamid, position: 'pitcher' }),
        },
        {
            queryKey: ['stealingAgainst', teamid, yearid, levelid],
            queryFn: (t) => Advance.stag(t, { teamid, yearid, levelid }),
        },
        {
            queryKey: ['pickoffs', teamid, yearid, levelid],
            queryFn: (t) => Advance.pickoff(t, { teamid, yearid, levelid }),
        },
        {
            queryKey: ['pitchouts', teamid, yearid, levelid],
            queryFn: (t) => Advance.pitchout(t, { teamid, yearid, levelid }),
        },
    ];
    return queries;
};

const mapStealingAgainstReport = (translate, response) => {
    const pitchers = response[0]?.data || [];
    const { pitchers: reportPitchers, bycount, pitcheravgtimes, pitchertimes, settimes, catcher, leagueaverage } = response[1]?.data || {};
    const { pitcherpkof, teampitcherpkof, mlbpitcherpkof, catcherpkof, teamcatcherpkof, mlbcatcherpkof } = response[2]?.data || {};
    const { PitchoutLog: pitchoutlog, PitchoutByCount: pitchoutbycount, PitchoutSummary: pitchoutsummary } = response[3]?.data || {};

    const playerCounts = bycount.reduce((acc, c) => ({ ...acc, [c.playerid]: c }), {});
    const pitcherObj = reportPitchers.reduce((acc, p) => ({ ...acc, [p.playerid]: p }), {});
    const pitcherAvgs = pitcheravgtimes.reduce((acc, avg) => ({
        ...acc,
        [avg.playerid]: roundPitcherAvgTimes(avg),
    }), {});

    const { pitchTimesChartConfig, setTimesChartConfig } = buildStealingAgainstTimeCharts(
        translate,
        pitchertimes,
        settimes,
    );

    return {
        pitchers,
        reportPitchers,
        pitcherObj,
        pitcherAvgs,
        playerCounts,
        bycount,
        pitchTimesChartConfig,
        setTimesChartConfig,
        catcher,
        leagueaverage,
        pitcherpkof,
        teampitcherpkof,
        mlbpitcherpkof,
        catcherpkof,
        teamcatcherpkof,
        mlbcatcherpkof,
        pitchoutlog,
        pitchoutbycount,
        pitchoutsummary,
    };
};

const StealingAgainstReport = ({ team }) => {
    const { t: translate } = useTranslation();
    const { CurrentYear } = useConfig();
    const singleYearOptions = window.$IsDugoutApp ? [CurrentYear] : [CurrentYear, CurrentYear - 1, CurrentYear - 2];
    const yearOptions = [
        { name: `${CurrentYear - 1} - ${CurrentYear}`, value: -1 },
        ...(singleYearOptions.map((y) => ({ name: y, value: y }))),
    ];
    const [year, setYear] = useState(yearOptions[1]); // default to current year
    const dataQueries = buildQueries(team.TeamID, year.value, team.LevelID);
    const dataQueriesResponse = useOktaQueries(dataQueries);

    if (!team || dataQueriesResponse.some((q) => q.isLoading)) {
        return <div style={{ margin: '10px' }}><LoadingSpinner /></div>;
    }

    const data = mapStealingAgainstReport(translate, dataQueriesResponse);

    const stateTabs = [
        {
            key: 'pitchers',
            title: translate('baseball.pitcher_other'),
            component: (
                <StealingAgainstPitchers data={data} levelid={team.LevelID} />
            ),
        }, {
            key: 'catchers',
            title: translate('baseball.catcher_other'),
            component: (
                <StealingAgainstCatchers data={data} />
            ),
        }, {
            key: 'pitchouts',
            title: translate('sections.opponent.team.pitchouts'),
            component: (
                <StealingAgainstPitchouts data={data} />
            ),
        },
    ];

    return (
        <StyledStealingAgainstReport>
            <div className="d-flex align-items-center justify-content-between mb-2">
                <h2 className="mb-0">{translate('sections.opponent.team.stealing-against-report')} { year.name }</h2>
                <Dropdown options={yearOptions} value={year} onSelect={setYear} align="end" />
            </div>
            <StateTabs tabs={stateTabs} key={year.value} />
        </StyledStealingAgainstReport>
    );
};

StealingAgainstReport.propTypes = {
    team: PropTypes.shape({
        LevelID: PropTypes.number,
        TeamID: PropTypes.number,
    }),
};
StealingAgainstReport.defaultProps = {
    team: {},
};

export default StealingAgainstReport;
